/* ============================================
   Header JS — Custom Elements
   ============================================ */
(function() {
  'use strict';

  /* --- StickyHeader --- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.header = document.querySelector('.section-header');
      this.setHeaderHeight();
      window.addEventListener('resize', this.setHeaderHeight.bind(this));
    }

    setHeaderHeight() {
      const logo = document.querySelector('.header__logo');
      if (logo) {
        document.documentElement.style.setProperty('--header-height', logo.offsetHeight + 'px');
      }
    }
  }
  if (!customElements.get('sticky-header')) {
    customElements.define('sticky-header', StickyHeader);
  }

  /* ============================================
     MegaMenuController — Single shared controller
     ============================================ */
  const MegaMenuController = {
    container: null,
    activeTrigger: null,
    closeTimeout: null,
    isReady: false,

    init() {
      if (this.isReady) return;
      this.container = document.querySelector('[data-megamenu-container]');
      if (!this.container) return;


      // Container hover — keep open
      this.container.addEventListener('mouseenter', () => {
        if (window.innerWidth < 990) return;
        clearTimeout(this.closeTimeout);
      });

      // Container leave — start closing
      this.container.addEventListener('mouseleave', () => {
        if (window.innerWidth < 990) return;
        this._startClose();
      });

      // Outside click — close
      document.addEventListener('click', (e) => {
        if (!this.activeTrigger) return;
        if (this.activeTrigger.contains(e.target)) return;
        if (this.container.contains(e.target)) return;
        this.close();
      });

      this.isReady = true;
    },

    open(triggerEl, panelId) {
      if (!this.container) return;
      clearTimeout(this.closeTimeout);

      const panel = this.container.querySelector(`[data-megamenu-panel="${panelId}"]`);
      if (!panel) return;

      const content = this.container.querySelector('.header-megamenu__content');
      const isAlreadyOpen = this.container.classList.contains('is-open');

      // Reset previous trigger state
      if (this.activeTrigger && this.activeTrigger !== triggerEl) {
        this.activeTrigger.setAttribute('data-open', 'false');
        const prevBtn = this.activeTrigger.querySelector('.header__menu-trigger');
        if (prevBtn) prevBtn.setAttribute('aria-expanded', 'false');
      }

      if (isAlreadyOpen && content) {
        // Switching panels — animate height
        const oldHeight = content.offsetHeight;

        this.container.querySelectorAll('.header-megamenu__panel').forEach(p => p.classList.remove('is-active'));
        panel.classList.add('is-active');

        const newHeight = content.scrollHeight;

        if (oldHeight !== newHeight) {
          content.style.height = oldHeight + 'px';
          content.offsetHeight;
          content.style.transition = 'height 0.35s cubic-bezier(0.32, 0.72, 0, 1)';
          content.style.height = newHeight + 'px';

          const onEnd = () => {
            content.style.height = '';
            content.style.transition = '';
            content.removeEventListener('transitionend', onEnd);
          };
          content.addEventListener('transitionend', onEnd);
        }
      } else {
        // First open
        this.container.querySelectorAll('.header-megamenu__panel').forEach(p => p.classList.remove('is-active'));
        panel.classList.add('is-active');
        this.container.classList.add('is-open');
      }

      document.body.classList.add('megamenu-open');
      this.activeTrigger = triggerEl;
      triggerEl.setAttribute('data-open', 'true');
      const btn = triggerEl.querySelector('.header__menu-trigger');
      if (btn) btn.setAttribute('aria-expanded', 'true');
    },

    _startClose() {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = setTimeout(() => this.close(), 400);
    },

    close() {
      if (!this.container) return;
      clearTimeout(this.closeTimeout);

      const wasOpen = this.container.classList.contains('is-open');

      this.container.classList.remove('is-open');
      document.body.classList.remove('megamenu-open');

      // Reset trigger state
      if (this.activeTrigger) {
        this.activeTrigger.setAttribute('data-open', 'false');
        const btn = this.activeTrigger.querySelector('.header__menu-trigger');
        if (btn) btn.setAttribute('aria-expanded', 'false');
      }

      if (wasOpen) {
        // Remove is-active after close transition
        const onEnd = () => {
          this.container.querySelectorAll('.header-megamenu__panel').forEach(p => p.classList.remove('is-active'));
          this.container.removeEventListener('transitionend', onEnd);
        };
        this.container.addEventListener('transitionend', onEnd);
      }

      this.activeTrigger = null;
    }
  };

  /* ============================================
     HeaderMenu — nav-menu custom element
     ============================================ */
  class HeaderMenu extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('.header__menu-trigger');
      if (!this.trigger) return;

      this._megaTarget = this.getAttribute('data-megamenu-target');
      this._dropdown = this.querySelector('.header__dropdown');
      if (!this._megaTarget && !this._dropdown) return;

      this._openTimeout = null;
      this._closeTimeout = null;

      // Init shared mega menu controller
      if (this._megaTarget) MegaMenuController.init();

      // Hover
      this.addEventListener('mouseenter', this._onEnter.bind(this));
      this.addEventListener('mouseleave', this._onLeave.bind(this));

      // Click
      this.trigger.addEventListener('click', this._onClick.bind(this));

      // Dropdown outside click
      if (this._dropdown) {
        document.addEventListener('click', (e) => {
          if (!this.contains(e.target)) this._closeDropdown();
        });
      }
    }

    _onEnter() {
      if (window.innerWidth < 990) return;

      if (this._megaTarget) {
        clearTimeout(MegaMenuController.closeTimeout);
        clearTimeout(this._openTimeout);
        this._openTimeout = setTimeout(() => {
          MegaMenuController.open(this, this._megaTarget);
        }, 100);
      } else if (this._dropdown) {
        clearTimeout(this._closeTimeout);
        this._openTimeout = setTimeout(() => this._openDropdown(), 100);
      }
    }

    _onLeave() {
      if (window.innerWidth < 990) return;
      clearTimeout(this._openTimeout);

      if (this._megaTarget) {
        // Don't close from trigger, let controller handle via container mouseleave
        if (MegaMenuController.container && MegaMenuController.container.classList.contains('is-open')) return;
        // Menu not open yet, cancel pending open
      } else if (this._dropdown) {
        this._closeTimeout = setTimeout(() => this._closeDropdown(), 300);
      }
    }

    _onClick(e) {
      e.preventDefault();

      if (this._megaTarget) {
        if (MegaMenuController.activeTrigger === this) {
          MegaMenuController.close();
        } else {
          MegaMenuController.open(this, this._megaTarget);
        }
      } else if (this._dropdown) {
        if (this._dropdown.classList.contains('is-open')) {
          this._closeDropdown();
        } else {
          // Close other dropdowns
          document.querySelectorAll('nav-menu').forEach(m => {
            if (m !== this && m._dropdown) m._closeDropdown();
          });
          MegaMenuController.close();
          this._openDropdown();
        }
      }
    }

    _openDropdown() {
      if (!this._dropdown) return;
      this._dropdown.classList.add('is-open');
      this.setAttribute('data-open', 'true');
      this.trigger.setAttribute('aria-expanded', 'true');
    }

    _closeDropdown() {
      if (!this._dropdown) return;
      this._dropdown.classList.remove('is-open');
      this.setAttribute('data-open', 'false');
      this.trigger.setAttribute('aria-expanded', 'false');
    }

    // Public close for StickyHeader.closeAll()
    close() {
      if (this._megaTarget) {
        MegaMenuController.close();
      } else {
        this._closeDropdown();
      }
    }

    isOpen() {
      if (this._megaTarget) {
        return MegaMenuController.activeTrigger === this;
      }
      return this._dropdown && this._dropdown.classList.contains('is-open');
    }
  }
  if (!customElements.get('nav-menu')) {
    customElements.define('nav-menu', HeaderMenu);
  }

  /* ============================================
     HeaderDrawer — mobile
     ============================================ */
  class HeaderDrawer extends HTMLElement {
    connectedCallback() {
      const drawerId = this.getAttribute('data-drawer-target');
      this.drawer = document.getElementById(drawerId);
      if (!this.drawer) return;

      this.panel = this.drawer.querySelector('.header-drawer__panel');
      this.submenuStack = [];

      this.hamburger = this.querySelector('[data-drawer-open]');
      this.hamburger?.addEventListener('click', () => this.toggle());
      // Close when clicking outside the panel (overlay area)
      document.addEventListener('click', (e) => {
        if (this.drawer.getAttribute('aria-hidden') !== 'false') return;
        if (this.panel && this.panel.contains(e.target)) return;
        if (this.hamburger && this.hamburger.contains(e.target)) return;
        this.close();
      });

      this.drawer.querySelectorAll('[data-submenu-open]').forEach(btn => {
        btn.addEventListener('click', () => this.openSubmenu(btn.getAttribute('data-submenu-open')));
      });
      this.drawer.querySelectorAll('[data-submenu-back]').forEach(btn => {
        btn.addEventListener('click', () => this.closeSubmenu());
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer.getAttribute('aria-hidden') === 'false') this.close();
      });
    }

    toggle() {
      if (this.drawer.getAttribute('aria-hidden') === 'false') {
        this.close();
      } else {
        this.open();
      }
    }

    open() {
      const header = this.drawer.closest('.header-wrapper')?.querySelector('.header');
      if (header) {
        const rect = header.getBoundingClientRect();
        this.drawer.style.setProperty('--header-drawer-top', `${rect.bottom}px`);
      }
      this.drawer.setAttribute('aria-hidden', 'false');
      this.hamburger?.classList.add('is-active');
      document.body.classList.add('header-drawer-open');
    }

    close() {
      this.submenuStack.forEach(id => {
        const sub = this.drawer.querySelector(`[data-submenu="${id}"]`);
        if (sub) sub.setAttribute('aria-hidden', 'true');
      });
      this.submenuStack = [];
      this.drawer.setAttribute('aria-hidden', 'true');
      this.hamburger?.classList.remove('is-active');
      document.body.classList.remove('header-drawer-open');
    }

    openSubmenu(id) {
      const sub = this.drawer.querySelector(`[data-submenu="${id}"]`);
      if (!sub) return;
      this.submenuStack.push(id);
      sub.setAttribute('aria-hidden', 'false');
    }

    closeSubmenu() {
      const id = this.submenuStack.pop();
      if (!id) return;
      const sub = this.drawer.querySelector(`[data-submenu="${id}"]`);
      if (sub) sub.setAttribute('aria-hidden', 'true');
    }
  }
  if (!customElements.get('header-drawer-nav')) {
    customElements.define('header-drawer-nav', HeaderDrawer);
  }

  /* ============================================
     Newsletter Modal
     ============================================ */
  const newsletterModal = document.querySelector('[data-newsletter-modal]');
  if (newsletterModal) {
    // Open triggers
    document.querySelectorAll('[data-newsletter-modal-open]').forEach(btn => {
      btn.addEventListener('click', () => {
        // If drawer is open, close it first
        const drawerNav = document.querySelector('header-drawer-nav');
        if (drawerNav?.drawer?.getAttribute('aria-hidden') === 'false') {
          drawerNav.close();
        }
        newsletterModal.setAttribute('aria-hidden', 'false');
        document.body.classList.add('overflow-hidden');
      });
    });

    // Close triggers
    newsletterModal.querySelectorAll('[data-newsletter-modal-close]').forEach(el => {
      el.addEventListener('click', () => {
        newsletterModal.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('overflow-hidden');
      });
    });

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && newsletterModal.getAttribute('aria-hidden') === 'false') {
        newsletterModal.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('overflow-hidden');
      }
    });
  }

  /* ============================================
     Search Modal Overlay
     ============================================ */
  document.querySelectorAll('.header__search--desktop details').forEach(details => {
    const megamenu = document.querySelector('[data-megamenu-container]');
    if (!megamenu) return;

    details.addEventListener('toggle', () => {
      if (details.open) {
        megamenu.classList.add('search-overlay');
      } else {
        megamenu.classList.remove('search-overlay');
      }
    });
  });
})();
